import * as XLSX from 'xlsx';
import { type ColumnDef } from './excelUtils';
import type { StockCount, StockCountLine } from '../types/venue';
import { fromMinorUnits } from './money';

/**
 * Stock Count export sheet.
 *
 * Produces an .xlsx for a stock count listing its current lines. System
 * Quantity (expectedQty) is reference; Physical Count reflects the line's
 * current countedQty (blank until counted). Unit Cost is a static value from
 * the getCount enrichment (snapshot.lastCost).
 *
 * STOCKCOUNT-SHEET.2 — Variance, Physical Value and Variance Value are written
 * as LIVE Excel formulas (not static values) so they recompute when the
 * operator types a Physical Count on site:
 *   Variance       = Physical Count - System Quantity
 *   Physical Value = Physical Count × Unit Cost
 *   Variance Value = Variance × Unit Cost
 * Formulas resolve to blank while Physical Count (or Unit Cost, for the value
 * columns) is empty.
 *
 * Re-import keying: Inventory Item ID is authoritative; Short ID is a
 * human-friendly secondary key when present. On import Inventory Item ID,
 * Physical Count and Notes are read — Unit Cost / Variance / Physical Value /
 * Variance Value are ignored (informational only).
 *
 * COUNT-LINE-NOTES.1 — Notes now round-trips. It was exported blank and
 * discarded on import, so an explanation written in the column the sheet
 * offered ("two behind the Barolo were empties") vanished on re-upload with no
 * warning. A blank cell on import leaves an existing note alone rather than
 * clearing it.
 */
export const STOCK_COUNT_EXPORT_COLUMNS: ColumnDef[] = [
  { header: 'Inventory Item ID', example: '' }, // A — authoritative re-import key
  { header: 'Short ID', example: '' },          // B — secondary human key (snapshot.shortId)
  { header: 'Item Name', example: '' },         // C — reference
  { header: 'Item Type', example: '' },         // D — reference (snapshot.itemType)
  { header: 'Size', example: '' },              // E — reference (snapshot.sizeValue)
  { header: 'Unit', example: '' },              // F — reference (snapshot.sizeUnit)
  { header: 'Location', example: '' },          // G — reference
  { header: 'System Quantity', example: '' },   // H — expectedQty (static)
  { header: 'Physical Count', example: '' },    // I — countedQty (static, blank until counted)
  { header: 'Variance', example: '' },          // J — FORMULA: I - H
  { header: 'Unit Cost', example: '' },         // K — snapshot.lastCost (static)
  { header: 'Physical Value', example: '' },    // L — FORMULA: I × K
  { header: 'Variance Value', example: '' },    // M — FORMULA: J × K (may be negative)
  { header: 'Notes', example: '' },             // N — optional
];

// Column indices (0-based) for the formula cells — must match the order above.
const COL_VARIANCE = 9;        // J
const COL_PHYSICAL_VALUE = 11; // L
const COL_VARIANCE_VALUE = 12; // M

/**
 * Build the export rows from already-loaded stock count lines and trigger a
 * client-side .xlsx download. No backend calls. Variance / Physical Value /
 * Variance Value are emitted as live Excel formulas; every other column is a
 * static value.
 */
/** Sanitize a string for safe use in a filename (spaces/specials → single dashes). */
function sanitizeFilePart(s: string): string {
  return s.trim().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}

/** YYYY-MM-DD from the count date (countedAt → postedAt → createdAt), else today. */
function exportDatePart(count: StockCount): string {
  const raw = count.countedAt ?? count.postedAt ?? count.createdAt ?? null;
  const d = raw ? new Date(raw) : new Date();
  const safe = Number.isNaN(d.getTime()) ? new Date() : d;
  return safe.toISOString().slice(0, 10);
}

/**
 * Human-readable export filename:
 *   stock-count-<location-name>-<YYYY-MM-DD>.xlsx   e.g. stock-count-General-Stock-2026-07-02.xlsx
 * Falls back to a short count id when no location name is available:
 *   stock-count-<short-count-id>-<YYYY-MM-DD>.xlsx
 */
export function buildStockCountExportFilename(count: StockCount, lines: StockCountLine[]): string {
  const datePart = exportDatePart(count);
  const rawLocation = lines.find(l => l.snapshot?.locationName)?.snapshot?.locationName ?? '';
  const locPart = sanitizeFilePart(rawLocation);
  if (locPart) return `stock-count-${locPart}-${datePart}.xlsx`;
  const shortId = String(count._id).slice(-8);
  return `stock-count-${shortId}-${datePart}.xlsx`;
}

export function downloadStockCountExport(count: StockCount, lines: StockCountLine[]): void {
  const headers = STOCK_COUNT_EXPORT_COLUMNS.map((c) => c.header);

  // Static value rows. The three formula columns are left blank here and set as
  // formula cells below so a re-typed Physical Count keeps them correct.
  const dataAoa: (string | number)[][] = lines.map((line) => {
    const lastCost = line.snapshot.lastCost ?? null;
    const counted = line.countedQty;
    return [
      line.inventoryItemId,
      line.snapshot.shortId ?? '',
      line.snapshot.displayName ?? '',
      line.snapshot.itemType ?? '',
      line.snapshot.sizeValue != null ? line.snapshot.sizeValue : '',
      line.snapshot.sizeUnit ?? '',
      line.snapshot.locationName ?? '',
      line.expectedQty,                    // H System Quantity
      counted != null ? counted : '',      // I Physical Count (blank until counted)
      '',                                  // J Variance — formula
      lastCost != null ? fromMinorUnits(lastCost) : '',    // K Unit Cost (minor→major)
      '',                                  // L Physical Value — formula
      '',                                  // M Variance Value — formula
      line.notes ?? '',                    // N Notes — round-trips back on import
    ];
  });

  const ws = XLSX.utils.aoa_to_sheet([headers, ...dataAoa]);

  // Inject PURE Excel formula cells (STOCKCOUNT-SHEET.2b). We intentionally do
  // NOT write a cached value (v): the cell is unambiguously a formula. Excel /
  // Google Sheets / LibreOffice all compute formula cells on open (automatic
  // calc mode) and again whenever the operator types a Physical Count. IF(...="")
  // keeps the cell blank while its inputs are blank. A cached value here
  // previously made already-counted rows look like static numbers.
  //
  // SheetJS's `f` field must NOT include a leading "=". We overwrite the empty
  // placeholder cells created by aoa_to_sheet so no stray value remains.
  lines.forEach((_line, i) => {
    const r = i + 1;      // 0-based sheet row (header is row 0)
    const R = i + 2;      // Excel 1-based row number
    ws[XLSX.utils.encode_cell({ c: COL_VARIANCE, r })] =
      { t: 'n', f: `IF(I${R}="","",I${R}-H${R})` };
    ws[XLSX.utils.encode_cell({ c: COL_PHYSICAL_VALUE, r })] =
      { t: 'n', f: `IF(OR(I${R}="",K${R}=""),"",I${R}*K${R})` };
    ws[XLSX.utils.encode_cell({ c: COL_VARIANCE_VALUE, r })] =
      { t: 'n', f: `IF(OR(J${R}="",K${R}=""),"",J${R}*K${R})` };
  });

  ws['!cols'] = STOCK_COUNT_EXPORT_COLUMNS.map((c) => ({ wch: Math.max(c.header.length + 4, 16) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Export');
  XLSX.writeFile(wb, buildStockCountExportFilename(count, lines));
}
